import React, { useState, useEffect, useCallback } from 'react';
import { OrganisationService, type WasteFactor } from '../../../../services/organisationService';
import { useCollaborativeForm } from '../../../../hooks/useCollaborativeForm';
import { CollaborationIndicator, UpdateAnimationOverlay } from '../../../CollaborationIndicator';

interface VariableEntryProps {
  title: string;
  labType: "wet" | "dry";
}

// Define the structure for form fields
interface FormField {
  label: string;
  displayText: string; // Text to show instead of dropdown
  inputType: "number" | "text";
  showCurrency?: boolean;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  isEmissionFactor?: boolean;
  disableInput?: boolean;
  // Additional properties for dynamic waste factors
  wasteFactorId?: number;
  // National average values (never shown to client)
  nationalAvgCost?: number;
  nationalAvgEmissions?: number;
}

// Office Waste fields for dry lab
const officeWasteFields: FormField[] = [
  { label: "E-waste", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
  { label: "General/Landfill", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
  { label: "Recycling", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
  { label: "Confidential", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
];

const electricityRateField: FormField = {
  label: "Electricity rate",
  displayText: "Cost per kWh",
  inputType: "number",
  showCurrency: true,
  min: 0,
  max: 100,
  step: 0.01,
  nationalAvgCost: 0.30,
};

interface EmissionsFactorOption {
  value: string;
  label: string;
  announcement: string;
}

const EMISSIONS_FACTOR_OPTIONS: EmissionsFactorOption[] = [
  { value: "national", label: "National average", announcement: "the National Average" },
  { value: "act", label: "Australian Capital Territory (ACT)", announcement: "ACT" },
  { value: "nsw", label: "New South Wales (NSW)", announcement: "NSW" },
  { value: "nt", label: "Northern Territory (NT)", announcement: "NT" },
  { value: "qld", label: "Queensland (QLD)", announcement: "QLD" },
  { value: "sa", label: "South Australia (SA)", announcement: "SA" },
  { value: "tas", label: "Tasmania (TAS)", announcement: "TAS" },
  { value: "vic", label: "Victoria (VIC)", announcement: "VIC" },
  { value: "wa", label: "Western Australia (WA)", announcement: "WA" },
];

const EXCLUDED_LABEL_PATTERNS: RegExp[] = [
  /estimated\s+quantity/i,
  /are\s+all\s+bins\s+easily\s+accessible/i,
  /checklist/i,
  /behavio(u)?ral/i,
  /qualitative/i,
  /\?/,
];

const NATIONAL_AVG_SENTINEL = "__USE_NATIONAL_AVG__";

const isLabelExcluded = (label: string) =>
  EXCLUDED_LABEL_PATTERNS.some(pattern => pattern.test(label));

// Laboratory Waste fields for wet lab
const labWasteFields: FormField[] = [
  { label: "Biohazard", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
  { label: "General/Landfill", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
  { label: "Recycling", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
  { label: "Confidential", displayText: "Cost per kg", inputType: "number", showCurrency: true, min: 0, max: 100, step: 0.01 },
];

// Helper component to render form fields
const FormFieldRow = ({ field, value, onChange }: {
  field: FormField;
  value?: string;
  onChange?: (value: string) => void;
}) => {
  const [inputValue, setInputValue] = useState<string>(value && value !== NATIONAL_AVG_SENTINEL ? value : '');
  const [nationalApplied, setNationalApplied] = useState<boolean>(value === NATIONAL_AVG_SENTINEL);

  useEffect(() => {
    if (value === NATIONAL_AVG_SENTINEL) {
      setNationalApplied(true);
      setInputValue('');
    } else {
      setNationalApplied(false);
      setInputValue(value || '');
    }
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleUseNationalAvg = () => {
    // Use the appropriate national average value
    const nationalValue = field.nationalAvgCost || field.nationalAvgEmissions;
    if (nationalValue !== undefined) {
      setNationalApplied(true);
      setInputValue('');
      if (onChange) {
        onChange(NATIONAL_AVG_SENTINEL);
      }
    }
  };

  const handleClearNationalAvg = () => {
    setNationalApplied(false);
    setInputValue('');
    if (onChange) {
      onChange('');
    }
  };

  const shouldRenderInput = !field.disableInput && !nationalApplied;
  const hasNationalAverage = field.nationalAvgCost !== undefined || field.nationalAvgEmissions !== undefined;

  return (
    <div className={`
      form-field-row
      grid grid-cols-1
      gap-3
      p-3
      sm:p-4
      lg:gap-4
      lg:p-3
      xl:grid-cols-12
      xl:items-center
      ${field.isEmissionFactor ? 'emission-factor-field' : 'regular-field'}
      border-b border-base-200 last:border-b-0
    `}>
      {/* Label Section */}
      <div className="label-section min-w-0 xl:col-span-3">
        <label className={`
          field-label
          block
          font-semibold
          text-base-content/90
          text-sm
          sm:text-base
          lg:text-base
          ${field.isEmissionFactor ? 'xl:pr-6' : ''}
        `}>
          {field.label}
        </label>
      </div>

      {/* Input and Controls Section */}
      <div className={`
        controls-section
        xl:col-span-9
        grid grid-cols-1 sm:grid-cols-12
        items-center
        gap-3 sm:gap-4 lg:gap-6
      `}>
        {/* Display Text */}
        <div className={`
          display-text
          text-sm text-base-content/70
          sm:col-span-4 lg:col-span-4 xl:col-span-4
        `}>
          {field.displayText}
        </div>

        {/* Input and Button Group */}
        <div className="input-group sm:col-span-8 lg:col-span-8 xl:col-span-8 flex items-center gap-2 sm:gap-3 lg:gap-4 flex-wrap sm:flex-nowrap justify-end">
          {shouldRenderInput && (
            <label
              className={`input input-bordered flex items-center gap-2 text-sm h-10 lg:h-10 flex-1 min-w-0 sm:max-w-xs md:max-w-sm lg:max-w-md`}
            >
              {field.showCurrency && <span className="opacity-70">$</span>}
              <input
                type={field.inputType}
                className="grow min-w-0 bg-transparent outline-none"
                name={field.label.toLowerCase().replace(/[^a-z0-9]/g, '_')}
                min={field.min}
                max={field.max}
                step={field.step}
                value={inputValue}
                onChange={handleInputChange}
                placeholder="Enter value"
              />
              {field.unit && (
                <span className="text-xs sm:text-sm opacity-70 whitespace-nowrap">{field.unit}</span>
              )}
            </label>
          )}

          {nationalApplied && (
            <div className="flex items-center gap-2">
              <span className="badge md:p-4 badge-outline badge-primary text-xs sm:text-sm whitespace-nowrap">National average applied</span>
            </div>
          )}

          {/* Use national avg button - only show if national average data exists */}
          {hasNationalAverage && (
            <button
              type="button"
              onClick={nationalApplied ? handleClearNationalAvg : handleUseNationalAvg}
              className="
                national-avg-btn
                btn btn-outline btn-primary btn-sm
                text-xs
                px-3
                h-9
                min-h-9
                whitespace-nowrap
                flex-shrink-0
              "
            >
              {nationalApplied ? 'Enter custom value' : 'Use national avg'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EmissionsFactorRow = ({ value, onChange }: {
  value?: string;
  onChange?: (value: string) => void;
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(value || 'national');

  useEffect(() => {
    setSelectedValue(value || 'national');
  }, [value]);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    onChange?.(newValue);
  };

  const selectedOption = EMISSIONS_FACTOR_OPTIONS.find(option => option.value === selectedValue) || EMISSIONS_FACTOR_OPTIONS[0];
  const announcementLabel = selectedOption ? selectedOption.announcement : 'the National Average';

  return (
    <div className="form-field-row grid grid-cols-1 gap-3 p-3 sm:p-4 lg:gap-4 lg:p-3 xl:grid-cols-12 xl:items-center border-b border-base-200 last:border-b-0">
      <div className="label-section min-w-0 xl:col-span-3">
        <label className="field-label block font-semibold text-base-content/90 text-sm sm:text-base lg:text-base">
          Emissions Factor
        </label>
      </div>
      <div className="controls-section xl:col-span-9 grid grid-cols-1 sm:grid-cols-12 items-center gap-3 sm:gap-4 lg:gap-6">
        <div className="display-text text-sm text-base-content/70 sm:col-span-4 lg:col-span-4 xl:col-span-4">
          Select a state or territory
        </div>
        <div className="input-group sm:col-span-8 lg:col-span-8 xl:col-span-8 flex items-center gap-2 sm:gap-3 lg:gap-4 flex-wrap sm:flex-nowrap justify-end">
          <select
            className="select select-bordered text-sm h-10 lg:h-10 flex-1 min-w-0 sm:max-w-xs md:max-w-sm lg:max-w-md"
            value={selectedValue}
            onChange={handleSelectChange}
          >
            {EMISSIONS_FACTOR_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="text-xs sm:text-sm text-base-content/70 whitespace-nowrap">
            {`Using ${announcementLabel} emissions factors`}
          </span>
        </div>
      </div>
    </div>
  );
};

const EnergySection = ({ formValues, onFieldChange }: {
  formValues?: Record<string, string>;
  onFieldChange?: (fieldKey: string, value: string) => void;
}) => {
  const emissionsValue = formValues?.emissions_factor || 'national';

  return (
    <div className="form-section bg-base-100 rounded-lg border border-base-300 mb-6 lg:mb-8 overflow-hidden">
      <div className="section-header bg-base-200 px-4 py-3 sm:px-6 sm:py-4 border-b border-base-300">
        <h4 className="section-title font-semibold text-base-content m-0 text-base sm:text-lg lg:text-xl">
          Energy & Green House Gases
        </h4>
      </div>
      <div className="section-content">
        <EmissionsFactorRow
          value={emissionsValue}
          onChange={(value) => onFieldChange?.('emissions_factor', value)}
        />
        <FormFieldRow
          field={electricityRateField}
          value={formValues?.electricity_rate}
          onChange={(value) => onFieldChange?.('electricity_rate', value)}
        />
      </div>
    </div>
  );
};

// Helper component to render a section - FULLY RESPONSIVE VERSION
const FormSection = ({ title, fields, formValues, onFieldChange }: {
  title: string;
  fields: FormField[];
  formValues?: Record<string, string>;
  onFieldChange?: (fieldLabel: string, value: string) => void;
}) => {
  const hasEmissionFactors = fields.some(field => field.isEmissionFactor);

  return (
    <div className={`
      form-section
      bg-base-100
      rounded-lg
      border
      border-base-300
      mb-6
      lg:mb-8
      overflow-hidden
      ${hasEmissionFactors ? 'emission-section' : 'regular-section'}
    `}>
      {/* Section Header */}
      <div className="section-header bg-base-200 px-4 py-3 sm:px-6 sm:py-4 border-b border-base-300">
        <h4 className={`
          section-title
          font-semibold
          text-base-content
          m-0
          text-base
          sm:text-lg
          lg:text-xl
          ${hasEmissionFactors ? 'text-center sm:text-left' : ''}
        `}>
          {title}
        </h4>
      </div>

      {/* Section Content */}
      <div className="section-content">
        {fields.map((field, index) => {
          const fieldKey = field.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
          return (
            <FormFieldRow
              key={index}
              field={field}
              value={formValues?.[fieldKey]}
              onChange={(value) => onFieldChange?.(fieldKey, value)}
            />
          );
        })}
      </div>
    </div>
  );
};

export const VariableForms = ({ title, labType }: VariableEntryProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [wasteFactors, setWasteFactors] = useState<WasteFactor[]>([]);
  const [nationalFactors, setNationalFactors] = useState<WasteFactor[]>([]);
  const [orgFactors, setOrgFactors] = useState<WasteFactor[]>([]);
  const [labWasteFactors, setLabWasteFactors] = useState<WasteFactor[]>([]);
  const [wasteFactorsLoading, setWasteFactorsLoading] = useState(true);
  const [wasteFactorsError, setWasteFactorsError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // Initialize collaborative form features
  const collaborativeState = useCollaborativeForm(
    formValues,
    {
      formType: 'variables',
      formId: `variables_${labType}`,
      enablePolling: false, // Disable automatic polling to prevent continuous API calls
      pollingInterval: 10000, // 10 seconds when enabled
      showUpdateNotifications: true,
      mergeStrategy: 'merge',
      onUpdate: (data) => {
        setFormValues(data);
      },
    }
  );

  const fetchWasteFactors = useCallback(async () => {
    try {
      setWasteFactorsLoading(true);
      setWasteFactorsError(null);

      // The API endpoint handles organisation identification through authentication
      const response = await OrganisationService.getWasteFactors();

      // Store national and organisation factors separately for proper processing
      const allFactors = [
        ...response.national_factors,
        ...response.organisation_factors,
        ...(response.lab_waste_factors || [])
      ];

      setWasteFactors(allFactors);
      setNationalFactors(response.national_factors);
      setOrgFactors(response.organisation_factors);
      setLabWasteFactors(response.lab_waste_factors || []);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch waste factors';
      setWasteFactorsError(errorMessage);
      console.error('❌ Error fetching waste factors:', error);
    } finally {
      setWasteFactorsLoading(false);
    }
  }, []);

  // Fetch waste factors on component mount
  useEffect(() => {
    fetchWasteFactors();
  }, [fetchWasteFactors]);

  // Centralized initialization logic for form values
  useEffect(() => {
    if (wasteFactorsLoading || (wasteFactors.length === 0 && labWasteFactors.length === 0)) return;

    setFormValues(prev => {
      const newValues = { ...prev };
      let hasChanges = false;

      // 1. Initialize from ALL waste factors (including national, org, and lab-specific)
      const allFactors = [...wasteFactors, ...labWasteFactors];

      allFactors.forEach(f => {
        const label = f.waste_type_row_label || f.category || '';
        if (!label) return;

        const fieldKey = label.toLowerCase().replace(/[^a-z0-9]/g, '_');

        // Detect if this factor is an override
        // For wasteFactors (national/org), check if it's in orgFactors
        // For labWasteFactors, check if cost_factor exists and is not null
        const isOrgOverride = orgFactors.some(org => org.id === f.id);
        const isLabOverride = labWasteFactors.some(lab => lab.id === f.id) && f.cost_factor !== null;

        if ((isOrgOverride || isLabOverride) && f.cost_factor !== null && f.cost_factor !== undefined) {
          if (newValues[fieldKey] === undefined) {
            newValues[fieldKey] = f.cost_factor.toString();
            hasChanges = true;
          }
        }

        // 2. Special mapping for Electricity rate & Emissions Factor (State selection)
        // We save these under "Eco Office" (or "Electricity rate" if it exists as such)
        if (label === 'Eco Office' || label === 'Electricity rate') {
          // A. Map Electricity Rate (cost_factor)
          // null cost_factor always means "use national average" — whether it's the national
          // default record or an org override that was explicitly reset to null.
          if (newValues['electricity_rate'] === undefined) {
            if (f.cost_factor !== null && f.cost_factor !== undefined) {
              newValues['electricity_rate'] = f.cost_factor.toString();
            } else {
              newValues['electricity_rate'] = NATIONAL_AVG_SENTINEL;
            }
            hasChanges = true;
          }

          // B. Map Emissions Factor State (emissions_factor index)
          if (newValues['emissions_factor'] === undefined && f.emissions_factor !== null && f.emissions_factor !== undefined) {
            const index = Math.round(f.emissions_factor);
            const option = EMISSIONS_FACTOR_OPTIONS[index];
            if (option) {
              newValues['emissions_factor'] = option.value;
              hasChanges = true;
            }
          }
        }
      });

      return hasChanges ? newValues : prev;
    });
  }, [wasteFactors, wasteFactorsLoading, orgFactors, labWasteFactors]);

  // Generate dynamic waste fields from API data
  const generateWasteFields = (nationalFactors: WasteFactor[], orgFactors: WasteFactor[]): FormField[] => {
    // Create a map of organization overrides by waste_type_row and category
    const orgOverrides = new Map<string, WasteFactor>();

    orgFactors.forEach(orgFactor => {
      const key = orgFactor.waste_type_row ?
        `row_${orgFactor.waste_type_row}` :
        `cat_${orgFactor.category}`;
      orgOverrides.set(key, orgFactor);
    });

    const fields: FormField[] = [];

    // Process national factors as the base, checking for organization overrides
    nationalFactors.forEach(nationalFactor => {
      const labelRaw = nationalFactor.waste_type_row_label || nationalFactor.category || 'Unknown';
      const label = typeof labelRaw === 'string' ? labelRaw.trim() : labelRaw;
      if (typeof label !== 'string' || label.length === 0) {
        return;
      }

      if (isLabelExcluded(label)) {
        return;
      }

      fields.push({
        label,
        displayText: "Cost per kg",
        inputType: "number" as const,
        showCurrency: true,
        min: 0,
        max: 100,
        step: 0.01,
        wasteFactorId: nationalFactor.id,
        // Store national values but never expose them to client
        nationalAvgCost: nationalFactor.cost_factor ?? 0,
        nationalAvgEmissions: nationalFactor.emissions_factor ?? 0
      });
    });

    return fields;
  };

  const handleSave = async () => {
    setIsLoading(true);
    setSaveStatus('idle');
    setSaveMessage('');

    try {
      let savedCount = 0;
      const savePromises: Promise<void>[] = [];

      // 0. Consolidate ALL waste factors by unique key
      // Include national, org, and lab-specific factors to ensure nothing is missed
      const allAvailableFactors = [
        ...wasteFactors,
        ...labWasteFactors,
      ];

      const uniqueFactorMap = new Map<string, { national?: WasteFactor; org?: WasteFactor; current?: WasteFactor }>();

      allAvailableFactors.forEach(f => {
        // Use waste_type_row as primary key, fallback to category + label or ID to ensure uniqueness
        const key = f.waste_type_row ? `row_${f.waste_type_row}` :
          f.category ? `cat_${f.category}_${f.waste_type_row_label || ''}` :
            `id_${f.id}`;

        const entry = uniqueFactorMap.get(key) || {};

        // Determine if this is the currently "active" factor for the UI
        // Org factors always override national ones.
        const isOrg = orgFactors.some(org => org.id === f.id);

        if (isOrg) {
          entry.org = f;
          entry.current = f;
        } else {
          entry.national = f;
          if (!entry.current) entry.current = f;
        }
        uniqueFactorMap.set(key, entry);
      });

      // Process each unique logical factor
      for (const entry of uniqueFactorMap.values()) {
        const factor = entry.current;
        if (!factor) continue;

        const label = factor.waste_type_row_label || factor.category || 'Unknown';
        const fieldKey = label.toLowerCase().replace(/[^a-z0-9]/g, '_');

        // Skip hardcoded energy fields as they are handled explicitly below
        if (fieldKey === 'electricity_rate' || fieldKey === 'emissions_factor') {
          continue;
        }

        const currentValue = formValues[fieldKey];

        // CASE 1: Reset to national default (NATIONAL_AVG_SENTINEL)
        if (currentValue === NATIONAL_AVG_SENTINEL) {
          if (entry.org) {
            savePromises.push(
              OrganisationService.saveWasteFactor({
                cost_factor: null, // As per API docs, null resets to national default
                emissions_factor: null,
                waste_type_row: factor.waste_type_row || undefined,
                category: factor.category || undefined,
              }).then(() => {
                savedCount++;
              })
            );
          }
          continue;
        }

        // CASE 2: Update with custom value
        if (currentValue !== undefined && currentValue !== '') {
          const parsedValue = parseFloat(currentValue);

          if (!isNaN(parsedValue) && parsedValue !== factor.cost_factor) {
            savePromises.push(
              OrganisationService.saveWasteFactor({
                category: factor.category || undefined,
                waste_type_row: factor.waste_type_row || undefined,
                cost_factor: parsedValue,
              }).then(() => {
                savedCount++;
              })
            );
          }
        }
      }

      // 1. Handle Energy Fields (Electricity Rate & Emissions Factor)
      // Consolidated into a single backend record under "Eco Office"
      const electricityValue = formValues.electricity_rate;
      const emissionsStateValue = formValues.emissions_factor || 'national';
      const emissionsIndexValue = EMISSIONS_FACTOR_OPTIONS.findIndex(opt => opt.value === emissionsStateValue);

      const energyEntry = Array.from(uniqueFactorMap.values()).find(e => {
        const label = (e.current?.waste_type_row_label || e.current?.category || '').toLowerCase();
        return label.includes('electricity') || label === 'eco office';
      });

      // Map current form values to backend format
      const formCostFactor = electricityValue === NATIONAL_AVG_SENTINEL ? null : (parseFloat(electricityValue || '0') || null);
      const formEmissionsFactor = emissionsIndexValue === -1 ? null : emissionsIndexValue;

      // Check if we actually need to save (diff from existing organisational factor)
      // organisational override is in energyEntry.org
      const needsSave = !energyEntry?.org ||
        formCostFactor !== (energyEntry.org.cost_factor ?? null) ||
        formEmissionsFactor !== (energyEntry.org.emissions_factor ?? null);

      if (needsSave) {
        // ALWAYS send both if we send either, to satisfy backend validation
        // and ensure the record state is fully synced. If both are null,
        // we send them to clear the override record (if backend permits).
        savePromises.push(
          OrganisationService.saveWasteFactor({
            category: "Eco Office",
            cost_factor: formCostFactor,
            emissions_factor: formEmissionsFactor,
          }).then(() => {
            savedCount++;
          })
        );
      }

      // Execute all saves in parallel
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
        await fetchWasteFactors();
      }

      setSaveStatus('success');
      setSaveMessage(`Variables saved successfully!${savedCount > 0 ? ` Updated ${savedCount} variable(s).` : ' No changes detected.'}`);

      setTimeout(() => {
        setSaveStatus('idle');
        setSaveMessage('');
      }, 3000);

    } catch (error) {
      setSaveStatus('error');
      setSaveMessage(error instanceof Error ? error.message : 'Failed to save variables. Please try again.');
      console.error('❌ Error saving variables:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (fieldKey: string, value: string) => {
    const newFormValues = { ...formValues, [fieldKey]: value };
    setFormValues(newFormValues);
    collaborativeState.updateLocalData(() => newFormValues);
  };

  // Individual save waste factor function removed - all saving now handled by main submit

  const Variables = labType === "wet" ? (
    <BronzeWetLabVariables
      wasteFactors={wasteFactors}
      nationalFactors={nationalFactors}
      orgFactors={orgFactors}
      wasteFactorsLoading={wasteFactorsLoading}
      wasteFactorsError={wasteFactorsError}
      generateWasteFields={generateWasteFields}
      formValues={formValues}
      onFieldChange={handleFieldChange}
    />
  ) : (
    <BronzeDryLabVariables
      wasteFactors={wasteFactors}
      nationalFactors={nationalFactors}
      orgFactors={orgFactors}
      wasteFactorsLoading={wasteFactorsLoading}
      wasteFactorsError={wasteFactorsError}
      generateWasteFields={generateWasteFields}
      formValues={formValues}
      onFieldChange={handleFieldChange}
      labWasteFactors={labWasteFactors}
    />
  );

  const handleSubmitClick = (event: React.FormEvent) => {
    event.preventDefault();
    setIsConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setIsConfirmOpen(false);
    await handleSave();
  };

  return (
    <div className="variable-forms-container">
      <form onSubmit={handleSubmitClick}>
        <div className="card shadow-sm bg-base-100 w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
          <div className="card-body w-full space-y-6 sm:space-y-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="card-title text-xl sm:text-2xl font-semibold text-base-content">{title}</h2>
              <CollaborationIndicator
                isConnected={collaborativeState.isConnected}
                hasUpdates={collaborativeState.hasRemoteUpdates}
                updateCount={collaborativeState.updateCount}
                lastUpdateBy={collaborativeState.lastUpdate?.updatedBy?.userName}
                onRefresh={collaborativeState.fetchLatestUpdates}
              />
            </div>

            <p className="text-sm sm:text-base text-base-content/70 leading-relaxed">
              If you want to use national averages and LAB ZER0 default metrics for these variables, leave any fields you don't want to customise blank and press "Save & Submit Variables" at the bottom.
              This will timestamp your current variables. The next time you press Save & Submit Variables, it will override your previous entry and update the backend algorithms.
            </p>

            {/* Status Messages */}
            {saveMessage && (
              <div className={`alert ${saveStatus === 'success' ? 'alert-success' : 'alert-error'} mb-2`}>
                <span>{saveMessage}</span>
              </div>
            )}

            <UpdateAnimationOverlay show={collaborativeState.updateAnimation}>
              {Variables}
            </UpdateAnimationOverlay>

            <div className="card-actions flex flex-col sm:flex-row items-center gap-4 pt-4 border-t border-base-300">
              <button
                type="submit"
                className={`btn btn-primary w-full sm:w-auto`}
                disabled={isLoading}
              >
                {isLoading ? 'Saving & Submitting...' : 'Save & Submit Variables'}
              </button>

              {saveStatus === 'success' && (
                <div className="flex items-center gap-2 text-success text-sm sm:text-base">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Saved</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>

      {isConfirmOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box space-y-4 max-w-md sm:max-w-lg">
            <h3 className="text-lg font-semibold text-base-content">Confirm submission</h3>
            <p className="text-sm text-base-content/80">
              Are you sure you want to save and submit these variables? This will timestamp your entries and overwrite any previously saved values.
            </p>
            <div className="modal-action flex flex-col sm:flex-row items-center gap-3">
              <button
                type="button"
                className="btn btn-outline w-full sm:w-auto"
                onClick={() => setIsConfirmOpen(false)}
                disabled={isLoading}
              >
                Return to Edit
              </button>
              <button
                type="button"
                className={`btn btn-primary w-full sm:w-auto ${isLoading ? 'loading' : ''}`}
                onClick={handleConfirmSubmit}
              >
                Yes – Save & Submit
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => setIsConfirmOpen(false)}>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
};

interface LabVariablesProps {
  wasteFactors: WasteFactor[];
  nationalFactors: WasteFactor[];
  orgFactors: WasteFactor[];
  wasteFactorsLoading: boolean;
  wasteFactorsError: string | null;
  generateWasteFields: (national: WasteFactor[], org: WasteFactor[]) => FormField[];
  formValues: Record<string, string>;
  onFieldChange: (field: string, value: string) => void;
  labWasteFactors?: WasteFactor[];
}

const BronzeDryLabVariables = ({ wasteFactors, nationalFactors, orgFactors, wasteFactorsLoading, wasteFactorsError, generateWasteFields, formValues, onFieldChange }: LabVariablesProps) => {
  // Use dynamic waste fields if available, otherwise fallback to static fields
  const dynamicOfficeWasteFields = wasteFactorsLoading
    ? []
    : wasteFactorsError
      ? officeWasteFields
      : (() => {
        // Filter national factors for office/general waste types
        const filteredNational = nationalFactors.filter(factor => {
          const label = (factor.waste_type_row_label || factor.category || '').toLowerCase();
          return label.includes('office') ||
            label.includes('general') ||
            label.includes('landfill') ||
            label.includes('e-waste') ||
            label.includes('ewaste') ||
            label.includes('electronic') ||
            label.includes('recycl') ||  // Matches recycle, recycling, recyclable
            label.includes('confidential');
        });

        // Filtered national waste factors for office waste

        const fields = generateWasteFields(filteredNational, orgFactors);

        // If no backend data matched, use static fields
        return fields.length > 0 ? fields : officeWasteFields;
      })();

  return (
    <div className="dry-lab-variables space-y-6 lg:space-y-8">
      {wasteFactorsError && (
        <div className="alert bg-amber-50 mb-4 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <div className="font-semibold">Error loading waste factors</div>
            <div className="text-sm">{wasteFactorsError}. Using default values.</div>
          </div>
        </div>
      )}
      {!wasteFactorsLoading && !wasteFactorsError && wasteFactors.length === 0 && (
        <div className="alert alert-info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>No waste factors found. Using default values.</span>
        </div>
      )}

      <FormSection
        title="Office Waste"
        fields={dynamicOfficeWasteFields.length > 0 ? dynamicOfficeWasteFields : officeWasteFields}
        formValues={formValues}
        onFieldChange={onFieldChange}
      />
      <EnergySection formValues={formValues} onFieldChange={onFieldChange} />
    </div>
  );
};

const BronzeWetLabVariables = ({ wasteFactors, nationalFactors, orgFactors, wasteFactorsLoading, wasteFactorsError, generateWasteFields, formValues, onFieldChange, labWasteFactors = [] }: LabVariablesProps) => {
  // Use dynamic waste fields if available, otherwise fallback to static fields
  const dynamicLabWasteFields = wasteFactorsLoading
    ? []
    : wasteFactorsError
      ? labWasteFields
      : (() => {
        // If we have lab_waste_factors from the new API structure, use them directly
        if (labWasteFactors && labWasteFactors.length > 0) {
          return labWasteFactors.map(factor => ({
            label: factor.waste_type_row_label || 'Unknown',
            displayText: "Cost per kg",
            inputType: "number" as const,
            showCurrency: true,
            min: 0,
            max: 100,
            step: 0.01,
            wasteFactorId: factor.id,
            nationalAvgCost: factor.national_cost_factor ?? 0,
            nationalAvgEmissions: factor.national_emissions_factor ?? 0
          }));
        }

        // Fallback to old logic: Include all national factors that have a non-empty waste_type_row_label
        // so any configured row labels from backend appear in Laboratory Waste.
        const filteredNational = nationalFactors.filter(factor =>
          typeof factor.waste_type_row_label === 'string' && factor.waste_type_row_label.trim().length > 0
        );

        // Filtered national waste factors for laboratory waste

        const fields = generateWasteFields(filteredNational, orgFactors);

        // If no backend data matched, use static fields
        return fields.length > 0 ? fields : labWasteFields;
      })();

  return (
    <div className="wet-lab-variables space-y-6 lg:space-y-8">
      {wasteFactorsError && (
        <div className="alert bg-amber-50 mb-4 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <div className="font-semibold">Error loading waste factors</div>
            <div className="text-sm">{wasteFactorsError}. Using default values.</div>
          </div>
        </div>
      )}
      {!wasteFactorsLoading && !wasteFactorsError && wasteFactors.length === 0 && (
        <div className="alert alert-info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>No waste factors found. Using default values.</span>
        </div>
      )}

      <FormSection
        title="Laboratory Waste"
        fields={dynamicLabWasteFields.length > 0 ? dynamicLabWasteFields : labWasteFields}
        formValues={formValues}
        onFieldChange={onFieldChange}
      />
      <EnergySection formValues={formValues} onFieldChange={onFieldChange} />
    </div>
  );
};